import { parseCmsDate, Weekdays, WEEKDAYS } from './Announcement';
import { moment } from '../../../vendor/moment';
import { isNullish } from '../../Utility/Nullish';

interface Time {
    start: string;
    end: string;
}

type Times = Array<Time>;

const MINUTE = {
    '12:00 AM': 0,
    '11:59 PM': 1439,
};

export class PlayTimes {
    #playTimes: Map<Weekdays, Times>;

    constructor() {
        this.#playTimes = new Map();
        WEEKDAYS.forEach((d) => this.#playTimes.set(d, []));
    }

    get hash(): string {
        return WEEKDAYS.map((d) => {
            const times = (this.#playTimes.get(d) ?? []).map((t) => {
                return `${t.start}-${t.end}`;
            });
            times.sort();
            return `${d}=` + times.join(';');
        }).join('|');
    }

    /**
     * Note: allows overnight announcements
     */
    addTimes(
        /** comma separated list */
        days: string,
        /** @see {@link parseCmsDate} */
        startTime: string,
        /** @see {@link parseCmsDate} */
        endTime: string
    ): void {
        const daysArray = days.replace(/ /g, '').split(',');

        for (let x = 0; x < daysArray.length; x++) {
            daysArray[x] = daysArray[x].trim();
        }

        const dayMinuteStart = parseTimeMinute(startTime);
        const dayMinuteEnd = parseTimeMinute(endTime);

        for (let x = 0; x < WEEKDAYS.length; x++) {
            if (daysArray.indexOf(WEEKDAYS[x]) !== -1) {
                const goesPastMidnight = dayMinuteEnd > 0 && dayMinuteEnd < dayMinuteStart;

                const times = this.#playTimes.get(WEEKDAYS[x]) ?? [];
                times.push({
                    start: startTime,
                    end: goesPastMidnight ? '11:59 PM' : endTime,
                });
                this.#playTimes.set(WEEKDAYS[x], times);

                if (goesPastMidnight) {
                    //if times goes past midnight
                    const addOne = x + 1 >= WEEKDAYS.length ? 0 : x + 1;
                    const times = this.#playTimes.get(WEEKDAYS[addOne]) ?? [];
                    times.push({
                        start: '12:00 AM',
                        end: endTime,
                    });
                    this.#playTimes.set(WEEKDAYS[addOne], times);
                }
            }
        }
    }

    canPlay(dateTime?: moment.Moment): boolean {
        //todo: convert to Luxon
        const now = isNullish(dateTime) ? moment() : dateTime;
        const dtNow = now.toDate();
        const sDayOfWeek = WEEKDAYS[dtNow.getDay()];
        const times = this.#playTimes.get(sDayOfWeek) ?? [];

        if (runAllDay(times)) {
            return true;
        }

        for (const playtime of times) {
            const startTime = parseTimeMoment(playtime.start);
            const endTime = parseTimeMoment(playtime.end);

            if (now.isBetween(startTime, endTime)) {
                return true;
            }
        }

        return false;
    }
}

function runAllDay(times: Times) {
    return (
        times.length === 1 &&
        parseTimeMinute(times[0].start) === MINUTE['12:00 AM'] &&
        parseTimeMinute(times[0].end) === MINUTE['11:59 PM']
    );
}

function parseTimeMinute(date: string) {
    const time = parseCmsDate(date);
    const hour = time[1];
    const minute = time[2];

    return hour * 60 + minute;
}

function parseTimeMoment(date: string) {
    const time = parseCmsDate(date);
    const timeString = `${time[1]}:${time[2]}:00`;

    return moment(timeString, 'H:mm:ss');
}

type AnnouncementId = number;

/**
 * key: {@link AnnouncementId} | value: {@link PlayTimes}
 */
export type PlayTimesMap = Map<AnnouncementId, PlayTimes>;

export function parsePlayTimes(
    announcements: Array<{
        announcement_id: unknown;
        run_days: unknown;
        run_stime: unknown;
        run_etime: unknown;
    }>
): PlayTimesMap {
    const playTimesMap: PlayTimesMap = new Map();

    announcements.forEach((annConfig) => {
        const annId = parseInt(String(annConfig.announcement_id));
        let entry = playTimesMap.get(annId);

        if (isNullish(entry)) {
            entry = new PlayTimes();
            playTimesMap.set(annId, entry);
        }

        entry.addTimes(
            String(annConfig.run_days),
            String(annConfig.run_stime),
            String(annConfig.run_etime)
        );
    });

    return playTimesMap;
}
